/**
 * /oauth/callback — receives the URL fragment that the backend wrote
 * after a successful OAuth round-trip. Parses tokens, stores access in
 * memory, and routes to the dashboard.
 *
 * The backend redirects here as:
 *   https://admin.taxor.co/oauth/callback?intent=login#access_token=...&refresh_token=...
 *   https://admin.taxor.co/oauth/callback?intent=login#error=oauth_error
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, XCircle } from 'lucide-react'

import { authApi, setAccessToken } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { parseCallbackFragment } from '@/lib/oauth'
import { markNeedsOrgSelection } from '@/lib/org-selection'

export function OAuthCallbackPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const intent = params.get('intent') ?? 'login'
  const [error, setError] = useState<string | null>(null)
  const handled = useRef(false)

  useEffect(() => {
    if (handled.current) return
    handled.current = true

    const payload = parseCallbackFragment(window.location.hash)

    // Wipe the fragment from history immediately so tokens don't sit
    // in browser history / shoulder-surfing range.
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname)
    }

    if (payload.error) {
      setError(humanReadableError(payload.error))
      return
    }

    // MFA-required path: provider verified, but the user has TOTP
    // enrolled. Hand off to LoginPage with the challenge token in
    // history state — LoginPage starts directly on the TOTP step.
    if (payload.mfa_required && payload.mfa_challenge_token) {
      navigate(ROUTES.LOGIN, {
        replace: true,
        state: {
          mfaChallenge: {
            token: payload.mfa_challenge_token,
            expiresIn: Number(payload.mfa_challenge_expires_in ?? 300),
            email: payload.email ?? '',
          },
        },
      })
      return
    }

    if (!payload.access_token || !payload.refresh_token) {
      setError('No recibimos tokens del proveedor.')
      return
    }

    setAccessToken(payload.access_token)
    // refresh_token would normally also be stored — for now we keep
    // tokens in memory only, so a page refresh forces re-login. When
    // we add silent refresh, persist refresh_token in httpOnly cookie.

    // Decidir destino. /select-org se muestra SIEMPRE que tenga ≥1
    // empresa: confirma entrada + ofrece acceso visible a "Mi cuenta".
    ;(async () => {
      try {
        const me = await authApi.me()
        if ((me.memberships?.length ?? 0) >= 1) {
          markNeedsOrgSelection()
          navigate(ROUTES.SELECT_ORG, { replace: true })
          return
        }
      } catch {
        // Si /auth/me falla, caemos al dashboard.
      }
      navigate(ROUTES.DASHBOARD, { replace: true })
    })()
  }, [navigate, intent])

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-bg-surface p-8 shadow-sm">
        {error ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-tint">
              <XCircle className="h-6 w-6 text-error" />
            </div>
            <h1 className="text-lg font-semibold text-fg-primary">No pudimos iniciarte sesión</h1>
            <p className="text-sm text-fg-secondary">{error}</p>
            <button
              type="button"
              onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
              className="mt-2 rounded-md bg-fg-primary px-4 py-2 text-sm font-medium text-bg-app hover:opacity-90"
            >
              Volver al login
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
            <h1 className="text-lg font-semibold text-fg-primary">Iniciando sesión…</h1>
            <p className="text-sm text-fg-secondary">Validando tu identidad con el proveedor.</p>
          </div>
        )}
      </div>
    </div>
  )
}

function humanReadableError(code: string): string {
  switch (code) {
    case 'invalid_or_expired_state':
      return 'La sesión de autorización expiró. Inténtalo de nuevo.'
    case 'provider_returned_no_email':
      return 'El proveedor no compartió tu correo. Verifica los permisos.'
    case 'access_denied':
      return 'Cancelaste la autorización en el proveedor.'
    case 'missing_code_or_state':
      return 'La respuesta del proveedor está incompleta.'
    case 'oauth_error':
      return 'Error en el proveedor. Inténtalo de nuevo.'
    case 'email_collision_existing_account':
      return (
        'Ya existe una cuenta en Taxor con este correo. Por seguridad, no podemos ' +
        'enlazarla automáticamente. Inicia sesión con tu contraseña (o tu otro proveedor) ' +
        'y conecta este proveedor desde Mi cuenta → Cuentas conectadas.'
      )
    case 'internal_error':
      return 'Tuvimos un problema interno. Inténtalo en un momento.'
    default:
      return `No pudimos completar la autorización (${code}).`
  }
}
